<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" :data="model"
                   :title="model.uuid ? $t(`Update Sale`) : $t(`Create New Sale`)"
                   @show="onShow" @hidden="onHidden" @ok.prevent="onSubmit"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading"
                   scrollable modal-class="mobile-full-screen">
      <div class="row pb-3">
        <div class="col-xl-7 border-right">
          <div class="row">
            <div class="col-md-4">
              <input-widget :hint="!model.uuid" :disabled="!!model.uuid" :model="model" attribute="sale_no"/>
            </div>
            <div class="col-md-12">
              <translatable-input-widget :hint="false" :model="model" :fields="model.getTranslatableFieldsArrayMain()"
                                         :errors="errors"/>
            </div>
            <div class="col-md-4">
              <department-select-input-widget :model="model" attribute="department_uuid"/>
            </div>
            <div class="col-md-4">
              <input-widget :model="model" attribute="currency_uuid" type="select2" value-field="uuid"
                            display-field="${item.code} (${item.symbol})" :items="currencies"/>
            </div>
            <div class="col-md-4">
              <input-widget :model="model" attribute="type" type="select2" :items="auctionTypes"
                            value-field="key" display-field="${item.text}" @select2Select="onSaleTypeChange"
                            @select2Unselect="onSaleTypeChange"/>
            </div>
            <div class="col-md-4">
              <input-widget :model="model" attribute="starting_paddle_number" type="number"/>
            </div>
            <div class="col-md-4">
              <input-widget :model="model" attribute="bid_cut_off_minutes" type="number"/>
            </div>
            <div class="col-md-4">
              <input-widget :model="model" attribute="hammer_target" type="number"/>
            </div>
            <div class="col-md-12">
              <user-select-input-widget multiple :model="model" attribute="account_managers_uuids"
                                        :options="{multiple: true}"/>
            </div>
            <div class="col-12">
              <input-widget :model="model" attribute="allow_webapp_condition_report_request" value-field="value"
                            display-field="${item.key}" type="select2" :items="allowWebappConditionReportRequests"/>
            </div>
          </div>
          <div class="row">
            <div class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="is_initial_deposit_amount" type="checkbox"
                            @change="onDepositAmountChange"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <b-tooltip target="is_listed_checkbox"
                         v-if="model.is_published"
                         :title="$t('All published auctions must be listed in the calendar')"/>
              <input-widget id="is_listed_checkbox" :model="model" attribute="is_listed" type="checkbox"
                            :disabled="model.is_published"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <input-widget @change="onPublishedChange" :model="model" attribute="is_published" type="checkbox"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="eligible_for_online_payments" type="checkbox"/>
            </div>
            <div v-if="model.type === 'timed'" class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="show_bidder_approval_status" type="checkbox"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="manual_bidder_approval" type="checkbox"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="use_images_from_lots" type="checkbox"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="require_credit_card_verification" type="checkbox"/>
            </div>
            <div v-if="hasArtisioTimedProvider" class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="require_identity_verification" type="checkbox"/>
            </div>
            <div class="col-md-auto d-flex align-items-center">
              <input-widget :model="model" attribute="is_private" type="checkbox"/>
            </div>
          </div>
          <div class="row">
            <div class="col-md-3 d-flex align-items-center">
              <input-widget v-if="model.is_initial_deposit_amount" :model="model"
                            attribute="initial_deposit_amount" type="number" placeholder="Amount"
                            :label="false" :rules="model.is_initial_deposit_amount ? 'required' : ''"/>
            </div>
          </div>
          <translatable-input-widget :hint="false" :model="model" :fields="model.sorted_dynamic_fields_template"
                                     :errors="errors"/>
          <div class="row">
            <div class="col-md-12">
              <hr class="mt-0 mb-2" style="font-weight: 800">
              <template v-if="model.type === 'live'">
                <h5>{{ $t('Sessions') }}</h5>
                <div class="row" v-for="(date, index) in model.dates" :key="`date_${index}`">
                  <div class="col-md-12">
                    <div class="row">
                      <div class="col-md-12">
                        <input-widget :label="false" :model="date" attribute="title" :placeholder="true"/>
                      </div>
                    </div>

                    <div class="row">
                      <div class="col-md-4">
                        <input-widget :model="date" attribute="start_date" type="datepicker" :min="getCurrentDate"/>
                      </div>
                      <div class="col-md-4">
                        <input-widget :model="date" attribute="start_time" type="timepicker"/>
                      </div>
                      <div class="col-md-4">
                        <input-widget :model="date" attribute="start_lot"/>
                      </div>
                    </div>
                  </div>
                  <div v-if="index > 0" class="col-md-1 text-left">
                    <b-tooltip :target="`remove-date-button-${index}`" :title="$t('Delete')"/>
                    <font-awesome-icon class="text-danger hover-cursor" :id="`remove-date-button-${index}`"
                                       :icon="'times'" @click="removeDateByIndex(index)"
                                       style="font-size: 1.5em;margin-top: .2em"/>
                  </div>
                  <div class="col-md-12">
                    <div v-if="index < model.dates.length - 1" class="mb-3"
                         style="border-bottom: 1px dashed #cecece"></div>
                  </div>
                </div>
                <b-button variant="link" class="pl-0 mb-2" @click="addNewDate">
                  <font-awesome-icon :icon="'plus-circle'"/>
                  {{ $t('Add New Day') }}
                </b-button>
              </template>

              <template v-else>
                <template v-if="!enableTimedSessions">
                  <h5>{{ $t('Session') }}</h5>
                  <div class="d-flex justify-content-between flex-wrap" v-for="(date, index) in model.dates"
                       :key="`date_${index}`">
                    <div>
                      <input-widget :model="date" attribute="start_date" type="datepicker" :min="getCurrentDate"/>
                    </div>
                    <div>
                      <input-widget :model="date" attribute="start_time" type="timepicker"/>
                    </div>
                    <div>
                      <input-widget :model="date" attribute="end_date" type="datepicker" :min="getCurrentDate"/>
                    </div>
                    <div>
                      <input-widget :model="date" attribute="end_time" type="timepicker"/>
                    </div>
                  </div>
                </template>

                <template v-else>
                  <h5>{{ $t('Sessions') }}</h5>
                  <div v-for="(date, index) in model.dates" :key="`date_${index}`">

                    <div class="row">
                      <div :class="index === 0 ? 'col-md-12' : 'col-md-11'">

                        <template v-if="index === 0">
                          <div class="row">
                            <div class="col-md-12">
                              <input-widget :label="false" :model="date" attribute="title" :placeholder="true"
                                            rules="required"/>
                            </div>
                          </div>
                          <div class="row">
                            <div class="col-md-12">
                              <div class="d-flex justify-content-between flex-wrap">
                                <div>
                                  <input-widget :model="date" attribute="start_date" type="datepicker"
                                                :min="getCurrentDate"/>
                                </div>
                                <div>
                                  <input-widget :model="date" attribute="start_time" type="timepicker"/>
                                </div>
                                <div>
                                  <input-widget :model="date" attribute="end_date" type="datepicker"
                                                :min="getCurrentDate"/>
                                </div>
                                <div>
                                  <input-widget :model="date" attribute="end_time" type="timepicker"/>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div class="row">
                            <div class="col-md-12">
                              <div class="d-flex justify-content-between flex-wrap">
                                <div style="flex: 2" class="mr-3">
                                  <input-widget :model="date" attribute="title" :placeholder="true" rules="required"/>
                                </div>
                                <div style="flex: 1" class="mr-3">
                                  <input-widget :model="date" attribute="end_date" type="datepicker"
                                                :min="getCurrentDate"/>
                                </div>
                                <div style="flex: 1">
                                  <input-widget :model="date" attribute="end_time" type="timepicker"/>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>

                      </div>

                      <div v-if="index > 0" class="col-md-1 text-left">
                        <b-tooltip :target="`remove-date-button-${index}`" :title="$t('Delete')"/>
                        <font-awesome-icon class="text-danger hover-cursor" :id="`remove-date-button-${index}`"
                                           :icon="'times'" @click="removeDateByIndex(index)"
                                           style="font-size: 1.5em;margin-top: 1.65em"/>
                      </div>

                    </div>


                    <div class="row">
                      <div class="col-md-12">
                        <div v-if="index < model.dates.length - 1" class="mb-3"
                             style="border-bottom: 1px dashed #cecece"></div>
                      </div>
                    </div>


                  </div>
                  <div>
                    <b-button variant="link" class="pl-0 mb-2" @click="addNewDate">
                      <font-awesome-icon :icon="'plus-circle'"/>
                      {{ $t('Add New Day') }}
                    </b-button>
                  </div>

                </template>

              </template>
            </div>
          </div>

          <div class="row">
            <div class="col-md-12">
              <hr class="mt-0 mb-2" style="font-weight: 800">
              <translatable-input-widget :hint="false" :model="model"
                                         :fields="model.getBranchAutoTranslatableFieldsArray('ckeditor')"
                                         :errors="errors"/>
            </div>
          </div>
        </div>
        <div class="col-xl-5">
          <h6>{{ $t('Images') }}</h6>
          <images-view :hide-save-btn="true" :sale-uuid="model.uuid" :run-polling-on-save="false" ref="imageViewer"/>
          <hr>
          <h6>{{ $t('Catalog') }}</h6>
          <file-uploader :multiple="false" :text="$t('Drag your catalogue here')" :show-preview="false"
                         :hint-text="false" :button-text="$t('Select Catalogue')" :items-prop="model.catalogs"
                         :accept="catalogAccept"/>
        </div>
      </div>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import SaleModel from "./Sale.model";
  import {createNamespacedHelpers} from "vuex";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import DepartmentSelectInputWidget from "../../core/widgets/DepartmentSelectInputWidget";
  import TranslatableInputWidget from "../../core/widgets/TranslatableInputWidget";
  import DateModel from "./Date.model";
  import SalesService from "./SalesService";
  import ImagesView from "./ImagesView";
  import FileUploader from "@/core/components/file-uploader/FileUploader";
  import {SYSTEM_DATE_FORMAT} from "@/core/services/formatter.service";
  import cloneDeep from "lodash/cloneDeep";
  import helperService from "@/core/services/helper.service";
  import UserSelectInputWidget from "@/core/widgets/UserSelectInputWidget";

  const {mapState: mapStateGlobal} = createNamespacedHelpers('global');
  const {mapState, mapActions} = createNamespacedHelpers('sales');

  export default {
    name: "SaleModal",
    components: {
      UserSelectInputWidget,
      FileUploader,
      ImagesView, TranslatableInputWidget, DepartmentSelectInputWidget, InputWidget, ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new SaleModel()
      }
    },
    computed: {
      ...mapStateGlobal(['currencies', 'currentBranch', 'locales', 'hasArtisioTimedProvider', 'enableTimedSessions', 'allowWebappConditionReportRequests']),
      ...mapState({
        modal: state => state.modal,
        auctionTypes: state => state.auctionTypes,
        editObject: state => state.modal.object,
        images: state => state.view.images
      }),
      catalogAccept() {
        return [{
          formats: ['application/pdf'],
          maxSize: 50 // TODO get size from settings
        }];
      },
      getCurrentDate() {
        return this.$options.filters.toDate(new Date(), SYSTEM_DATE_FORMAT)
      },
    },
    watch: {
      editObject() {
        if (this.editObject && this.editObject.uuid) {

          if (this.enableTimedSessions && this.editObject.type === 'timed' && this.editObject.dates && this.editObject.dates.length > 1) {
            this.editObject.dates = helperService.orderDatesByKey(this.editObject.dates, 'end_date')
          }

          this.model = new SaleModel(this.editObject);

          if (this.model.catalogs.length > 0) {
            this.model.catalogs.forEach((catalog) => {
              catalog.file = null;
            });
          }
        }
      },
      'model.type'() {
        if (this.model.dates.length > 1 && this.model.type === 'timed') {
          this.model.dates = [this.model.dates[0]];
        }
        this.model.initializeDynamicFields()
      },
    },
    methods: {
      ...mapActions(['hideModal', 'createSale', 'updateSale']),
      onShow() {
        this.showConfirmation = true;
        if (!this.model.branch) {
          this.model.branch = _.cloneDeep(this.currentBranch)
        }
        if (!this.editObject && this.model.branch && this.model.branch.auction_dynamic_fields_template) {
          this.model.initDynamicFields(this.model, this.model.branch.auction_dynamic_fields_template[this.model.type])
        }
        if (!this.model.uuid) {
          this.copyTranslatableFieldsFromBranch();
        }
      },
      onHidden() {
        this.model = new SaleModel();
        this.hideModal();
      },
      copyTranslatableFieldsFromBranch() {
        this.model.constructor.BRANCH_AUTO_TRANSLATABLE_FIELDS.forEach(field => {
          if (this.model.branch[field] && typeof this.model.branch[field] === 'object' && Object.keys(this.model.branch[field]).length > 0) {
            this.model[field] = this.model.branch[field];
          }
        });
      },

      copyDynamicFieldsToOtherLanguagesIfNecessary() {
        if (!this.locales || !this.locales.available_locales || Object.keys(this.locales.available_locales).length < 2
          || !this.model.sorted_dynamic_fields_template || this.model.sorted_dynamic_fields_template.length === 0) {
          return;
        }

        this.model.assignToDynamicFields()
        let masterLocale = this.locales.master_language.key
        let filledItem = this.model.dynamic_fields[masterLocale]

        for (let locale in this.model.dynamic_fields) {
          if (locale === masterLocale) {
            continue;
          }
          for (let fieldKey in filledItem) {
            let dfObj = this.model.sorted_dynamic_fields_template.find(f => f.key === fieldKey);
            if (dfObj && dfObj.translatable && this.model[`${fieldKey}_${locale}`]) {
              continue
            }
            this.model[`${fieldKey}_${locale}`] = filledItem[fieldKey]
          }
        }
      },
      copyTranslatableFieldsToOtherLanguage() {
        if (!this.locales || !this.locales.available_locales || Object.keys(this.locales.available_locales).length < 2) {
          return
        }

        let filledTitle = null;
        let filledDescription = null;

        for (let locale in this.locales.available_locales) {
          if (this.model[`title_${locale}`]) {
            filledTitle = this.model[`title_${locale}`]
          }
          if (this.model[`description_${locale}`]) {
            filledDescription = this.model[`description_${locale}`]
          }
        }

        if (filledTitle) {
          for (let locale in this.locales.available_locales) {
            if (!this.model[`title_${locale}`]) {
              this.model[`title_${locale}`] = filledTitle
            }
          }
        }

        if (filledDescription) {
          for (let locale in this.locales.available_locales) {
            if (!this.model[`description_${locale}`]) {
              this.model[`description_${locale}`] = filledTitle
            }
          }
        }
      },
      async onSubmit() {
        this.copyTranslatableFieldsToOtherLanguage()
        this.copyDynamicFieldsToOtherLanguagesIfNecessary()

        this.$nextTick(() => {
          this.runSubmitLogic()
        });
      },
      async runSubmitLogic() {
        await this.$refs.observer.reset()
        let validateSuccess = await this.$refs.observer.validate()
        if (!validateSuccess) {
          return
        }

        let response;

        this.loading = true;
        this.model.resetErrors()
        this.model.dates.forEach(d => d.resetErrors())
        this.model.assignToDynamicFields();
        // Assigning start date from first sessions because start and end dates are required in the API
        if (this.enableTimedSessions && this.model.dates.length > 1) {
          for (let i = 1; i < this.model.dates.length; i++) {
            this.model.dates[i].start_date = this.model.dates[0].start_date
            this.model.dates[i].start_time = this.model.dates[0].start_time
          }
        }

        if (this.enableTimedSessions && this.model.dates && this.model.dates.length > 1) {
          let tmpDates = cloneDeep(this.model.dates)
          let previousDate = null
          for (let index in tmpDates) {
            const date = tmpDates[index].toJSON()
            const currentDate = new Date(date.end_date)
            if (currentDate < previousDate) {
              this.model.dates[index].setMultipleErrors({end_date: [this.$i18n.t("End date should be more than previous session end date")]})
              this.loading = false;
              return
            }
            previousDate = currentDate
          }
        }
        if (!this.model.uuid) {
          response = await this.createSale(this.model);
          if (response.success) {
            await this.handleCatalogs(response.body.uuid);
          }
        } else {
          await this.handleCatalogs(this.model.uuid);
          response = await this.updateSale(this.model);
        }

        if (response.success) {

          let continueLogic = true;

          if (this.images.length > 0) {
            // Returns boolean if images were save in backend
            continueLogic = await this.$refs.imageViewer.save(response.body.uuid);
          }

          this.loading = false;

          if (continueLogic) {
            this.showConfirmation = false;
            this.hideModal();
            if (!this.model.uuid) {
              this.$toast('success', this.$t('Sale was successfully created'));
              this.$router.push({
                name: 'sales.view',
                params: {id: response.body.uuid},
              });
            } else {
              this.$toast('success', this.$t('Sale was successfully updated'));
            }
          }

        } else {

          this.loading = false;

          if (response.toastError) {
            this.$errorDialog(response.toastError);
          } else if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            if (response.body.dates) {
              for (let index in response.body.dates) {
                if (!this.enableTimedSessions) {
                  this.model.dates[index].setMultipleErrors(response.body.dates[index]);
                } else {
                  if (index === 0) {
                    this.model.dates[index].setMultipleErrors(response.body.dates[index]);
                  } else {
                    if (response.body.dates[index].start_date) {
                      this.model.dates[index].setMultipleErrors({end_date: response.body.dates[index].start_date});
                    } else {
                      this.model.dates[index].setMultipleErrors(response.body.dates[index]);
                    }
                  }
                }
              }
            } else {
              this.model.setMultipleErrors(response.body);
            }
          }
        }
      },
      addNewDate() {
        this.model.dates.push(new DateModel());
      },
      removeDateByIndex(index) {
        for (let i = 0; i < this.model.dates.length; i++) {
          if (i === index) {
            this.model.dates.splice(i, 1);
            break;
          }
        }
      },
      onPublishedChange(val) {
        if (val === true) {
          this.model.is_listed = true;
        }
      },
      onDepositAmountChange(val) {
        if (val === false) {
          this.model.initial_deposit_amount = null;
        }
      },
      handleCatalogs(saleUuid) {
        return new Promise(async (resolve, reject) => {
          try {
            const promises = [];
            const needsCatalogCreation = this.model.catalogs.find((item) => item.uuid === null);
            const catalogsToDelete = this.model.catalogs.filter((item) => item.to_be_deleted === true);

            if (needsCatalogCreation) {
              let formData = new FormData();
              this.model.catalogs.forEach((item) => {
                if (!item.uuid) formData.append('catalog', item.file);
              });
              formData.append('auction_uuid', saleUuid);
              promises.push(SalesService.createCatalogs(formData));
            }

            if (catalogsToDelete.length > 0) {
              promises.push(SalesService.removeCatalog(saleUuid, catalogsToDelete.map(catalog => catalog.uuid)));
            }

            let response = await Promise.all(promises);

            // Check if catalogs were created and afterwards remove old ones without uuids and add newly created ones
            if (needsCatalogCreation && response[0] && response[0].body) {
              let responseCatalogs = response[0].body;

              // Removing all elements from images which do not have uuid (not uploaded ones)
              this.model.catalogs = this.model.catalogs.filter((item) => {
                return item.uuid !== null;
              });

              responseCatalogs.forEach((catalog) => {
                // Pushing newly uploaded images to images array
                catalog.file = null;
                this.model.catalogs.push(catalog);
              });
            }

            resolve(true);
          } catch (e) {
            reject(e);
          }
        });
      },
      onSaleTypeChange() {
        this.model.dates = [new DateModel()];
      },
    }
  }
</script>

<style scoped>

</style>

